import { Progress } from '@base-ui/react/progress';
import { cn } from './cn';

// Full class strings per fill (never build class names dynamically).
const FILL = {
  default: 'bg-progress-fill',
  group: 'bg-progress-fill-group',
  highlight: 'bg-progress-fill-highlight',
  chart: 'bg-chart-bar',
  'member-complete': 'bg-member-progress-complete',
  'member-partial': 'bg-member-progress-partial',
  reading: 'bg-exercise-reading',
  writing: 'bg-exercise-writing',
  math: 'bg-exercise-math',
} as const;

/**
 * DESIGN.md › progress-bar: 16px track, orange fill. `value` is 0..1. `label` is the translated
 * accessible name — text lives in the app, never here. The fill is never narrower than it is
 * tall, so 0 % still shows a dot at the start. `track="surface"` is the white track on a
 * tinted card.
 */
export function ProgressBar({
  value,
  label,
  track = 'default',
  fill = 'default',
  className,
}: {
  value: number;
  label: string;
  track?: 'default' | 'surface';
  /** `group` is the olive fill of the group overview card, `highlight` a Groups card's blue. */
  fill?: keyof typeof FILL;
  className?: string;
}) {
  const percent = Math.round(Math.min(1, Math.max(0, value)) * 100);
  return (
    <Progress.Root value={percent} aria-label={label} className={cn('w-full', className)}>
      <Progress.Track
        className={cn(
          'h-4 w-full overflow-hidden rounded-full',
          track === 'surface' ? 'bg-surface' : 'bg-progress-track',
        )}
      >
        <Progress.Indicator
          className={cn('h-full min-w-4 rounded-full transition-[width]', FILL[fill])}
        />
      </Progress.Track>
    </Progress.Root>
  );
}
